import { useState } from 'react';
import { Button, Card, Group, Modal, Select, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { ROLES, useChangeRole, useRemoveMember, type Member, type Role } from '@/api/orgs';

export interface MembersTableProps {
  orgId: string;
  members: Member[];
  /** Owners (client pages) or staff (staff pages) can change roles and remove members. */
  canManage: boolean;
}

/** Member list with inline role editing and removal; read-only rows when `canManage` is false. */
export function MembersTable({ orgId, members, canManage }: MembersTableProps) {
  const { t } = useTranslation();
  const changeRole = useChangeRole(orgId);
  const removeMember = useRemoveMember(orgId);
  const [toRemove, setToRemove] = useState<Member | null>(null);

  const handleRoleChange = async (userId: string, role: Role) => {
    try {
      await changeRole.mutateAsync({ userId, role });
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  const handleRemove = async () => {
    if (!toRemove) return;
    try {
      await removeMember.mutateAsync(toRemove.user.public_id);
      setToRemove(null);
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  return (
    <Card p={0}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('auth.email')}</Table.Th>
            <Table.Th>{t('org.fullName')}</Table.Th>
            <Table.Th>{t('org.role')}</Table.Th>
            {canManage && <Table.Th />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {members.map((m) => (
            <Table.Tr key={m.user.public_id}>
              <Table.Td>{m.user.email}</Table.Td>
              <Table.Td>{m.user.full_name}</Table.Td>
              <Table.Td>
                {canManage ? (
                  <Select
                    aria-label={t('org.role')}
                    data={ROLES.map((r) => ({ value: r, label: t(`org.roles.${r}`) }))}
                    value={m.role}
                    allowDeselect={false}
                    onChange={(value) => value && void handleRoleChange(m.user.public_id, value as Role)}
                  />
                ) : (
                  <Text>{t(`org.roles.${m.role}`)}</Text>
                )}
              </Table.Td>
              {canManage && (
                <Table.Td>
                  <Button color="red" variant="light" onClick={() => setToRemove(m)}>
                    {t('common.remove')}
                  </Button>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={!!toRemove} onClose={() => setToRemove(null)} title={t('common.remove')}>
        <Stack gap="md">
          <Text>{t('org.confirmRemove', { name: toRemove?.user.full_name || toRemove?.user.email })}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setToRemove(null)}>
              {t('common.cancel')}
            </Button>
            <Button color="red" loading={removeMember.isPending} onClick={() => void handleRemove()}>
              {t('common.remove')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
